import store from '../app/ReduxStore';
import reducer from '../app/Reducers';
import React from "react";
import joinClassNames from "@utahdts/dts-react-common/common/JoinClassNames";
import he from 'he';

/**
 * Generic redux dispatcher
 * @param payload object
 * @param payload.path string Path to the field to modify
 * @param payload.field string Field that you need to modify
 * @param payload.value any Value of the field
 */
export function dispatchUpdateData(payload) {
    store.dispatch({
        type: reducer.ACTION_TYPES.UPDATE_DATA,
        payload: payload
    });
}

export function parseUrl(url, defaultValues) {
    const matches = /^\/(\d{4}|latest)\/?(\d+)?\/?(\w+)?\/?(\d+)?(\/text)?(\/draft)?$/.exec(url);
    let years = defaultValues.years;
    let year = defaultValues.currentFiscalYear;
    let cobiId = defaultValues.cobiId;
    let tab = defaultValues.tab;
    let issueKey = defaultValues.issueKey;
    let issueText = defaultValues.issueText;
    let draft = defaultValues.draft;
    let redirect = false;
    if(matches && years) {
        year = years.includes(Number(matches[1])) ? Number(matches[1]) : year;
        cobiId = Number(matches[2]) ? Number(matches[2]) : cobiId;
        tab = matches[3] && Object.values(TABS_STRUCTURE).includes(matches[3]) ? matches[3] : tab;
        issueKey = Number(matches[4]) && matches[3] === TABS_STRUCTURE.ISSUES ? Number(matches[4]) : issueKey;
        issueText = matches[5] ? true : issueText;
        draft = matches[6] ? true : draft;
        redirect = !years.includes(Number(matches[1]));
    } else {
    	redirect = true;
	}
    return {
        year: year,
        cobiId: cobiId,
        tab: tab,
		issueKey: issueKey,
		issueText: issueText,
		draft: draft,
		redirect: redirect,
    };
}

export function parseFundUrl(url, defaultValues) {
	const matches = /^\/fund\/(.+)$/.exec(url);
	let fundId = defaultValues.fundId;
	if(matches) {
		fundId = matches[1] ? matches[1] : fundId;
	}
	return {
		fundId: fundId
	};
}

export function parseAdminUrl(url, defaultValues) {
	const matches = /^\/admin\/editor\/(\d{4})\/(\d+)\/(\w+)$/.exec(url);
	let years = defaultValues.years;
	let year = defaultValues.currentEditorFiscalYear;
	let cobiId = defaultValues.cobiId;
	let tab = defaultValues.tab;
	if(matches) {
		year = years.includes(Number(matches[1])) ? Number(matches[1]) : year;
		cobiId = Number(matches[2]) ? Number(matches[2]) : cobiId;
		tab = matches[3] && Object.values(TABS_STRUCTURE).includes(matches[3]) ? matches[3] : tab;
	}
	return {
		year: year,
		cobiId: cobiId,
		tab: tab
	};
}

export const COBI_HIERARCHY = {
	SUBCOMMITTEE: 'subCommittee',
	AGENCY: 'agency',
	LINE_ITEM: 'lineItem',
	PROGRAM: 'program',
};

export const TABS_STRUCTURE = {
    OVERVIEW: 'overview',
    ISSUES: 'issues',
    PERFORMANCE: 'performance',
    BACKGROUND: 'background',
    FINANCIALS: 'financials'
};

export const COBI_ROLES = {
	ADMIN: 'ADMIN',
	SUPERVISOR: 'SUPERVISOR',
	ANALYST: 'ANALYST',
	NONE: 'NONE'
};

export const MAX_CHARACTERS = 350;
export const MAX_EDITOR_CHARACTERS = 4000;

export const COLOR_PALETTE = ["#44AA99", "#117733", "#999933", "#DDCC77", "#88CCEE", "#AA4499", "#CC6677", "#882255", "#332288"];

export function mapMenu(value) {
	let menuArray = [];
	// combine all 4 menus into a single array
	for (const menu in value) {
		if (value.hasOwnProperty(menu) && value[menu]) {
			menuArray = menuArray.concat(value[menu]);
		}
	}
	// map each object to the cobiId
	return menuArray.reduce((obj, cur) => ({...obj, [cur.cobiId]: cur}), {});
}

export function formatAmount(amount, decimal, prefix, suffix, parensForNegatives) {
	const isNumber = typeof amount === 'number';
	let useAmount = amount;
	if (isNumber) {
		if (parensForNegatives && amount < 0) {
			amount *= -1;
			prefix = `(${prefix || ''}`;
			suffix = `)${suffix || ''}`;
		}
		decimal = useAmount % 1 !== 0 ? decimal : 0;
		// not quite sure about the regex... \B word boundary; ?= lookahead; ...? :shrug:
		useAmount =  Number(amount).toFixed(decimal).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	}

	return `${prefix || ''}${useAmount}${suffix || ''}`;
}

/**
 * Converts a year into an FYXX-XX year format.
 * @param year Should can be a two digit or four digit number, e.g. 18 or 2018
 * @return {*|string}
 */
export function formatCobiYear(year) {
	let fyString = "";
	if (year) {
		const twoDigitYear = /^(\d{2})?(\d{2})/g.exec(String(year))[2];
		const yearPlusOne = Number(twoDigitYear) + 1;
		fyString = `FY${twoDigitYear}-${yearPlusOne}`;
	}
	return fyString;
}

export function buildDropDownOptions(years) {
	let array = [];

	years.fiscalYears.forEach((y) => {
        array.push({value: y.sessionFY, label: formatCobiYear(y.sessionFY)});
	});

	return array;
}

export const MEDIA_SIZES = {
	TABLET_NARROW_MIN_WIDTH: "only screen and (min-width: 768px), print",
	TABLET_NARROW_MAX_WIDTH: "only screen and (max-width: 767px)"
};

export function formatPriority(priority) {
	return (priority === -10 ? '*' : (priority <= 0 || priority > 98) ? '' : priority);
}

export function prioritySorting(a, b) {
	a = a === -10 ? a : a <= 0 ? Infinity : a;
	b = b === -10 ? b : b <= 0 ? Infinity : b;
	return a === b ? 0 : a === null ? -1 : b === null ? 1 : a - b;
}

export function footerNotice(classNames) {
	return (
		<p className={joinClassNames("note atid_footer-disclaimer", classNames)}>
			COBI contains unaudited data as presented to the Legislature by state agencies at the time of publication. For audited financial data see the State of Utah's <a href="https://le.utah.gov/CobiServlet/cobi/BudgetRD?key=cafr" target="_blank">Annual Comprehensive Financial Report</a>.
		</p>
	);
}

export function disableScroll() {
	document.body.classList.add('no-scroll');
}

export function enableScroll() {
	document.body.classList.remove('no-scroll');
}

export function capitalize(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export function contentLength(html, count) {
	let parser = new DOMParser();
	let doc = parser.parseFromString(html, "text/html");
	return doc.body.innerText.length > count;
}

export function isAllowed() {
	let rolesAllowed = Object.assign({}, COBI_ROLES);
	delete rolesAllowed.NONE;
	return Object.values(rolesAllowed).includes(store.getState().userAccount.roleName);
}

export function isApprover() {
	let rolesAllowed = Object.assign({}, COBI_ROLES);
	delete rolesAllowed.NONE;
	delete rolesAllowed.ANALYST;
	return Object.values(rolesAllowed).includes(store.getState().userAccount.roleName);
}

export function compareTextHtml(a, b) {
	let aText = he.decode(a);
	let bText = he.decode(b);
	return aText === bText;
}
